import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { Search, CalendarDays, BookOpen } from "lucide-react";

function formatDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });
}

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = "Travel Blog & Stories | Global Compass Tours & Travels Kolkata";
  }, []);

  useEffect(() => {
    setLoading(true);
    const handle = setTimeout(() => {
      const params = search ? { search } : {};
      api.get("/blog", { params }).then((r) => setPosts(r.data)).finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(handle);
  }, [search]);

  return (
    <div className="bg-ivory min-h-[80vh]">
      <div className="bg-maroon text-amber-50 py-14 relative overflow-hidden">
        <div className="absolute inset-0 grain pointer-events-none opacity-30" />
        <div className="relative max-w-7xl mx-auto px-4">
          <div className="text-amber-300 text-sm font-semibold tracking-widest uppercase mb-3">Stories, tips & travel journals</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight">Global Compass Travel Blog</h1>
          <p className="mt-4 text-amber-50/90 text-lg max-w-2xl">Destination guides, trip write-ups and practical advice from our Kolkata travel specialists.</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 -mt-8 relative z-10">
        <div className="bg-white rounded-2xl shadow-xl border border-amber-900/10 p-5">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search posts by topic, place or keyword…"
              className="w-full h-12 pl-10 pr-4 rounded-lg border-2 border-amber-900/15 focus:border-amber-600 focus:outline-none bg-ivory text-base"
              data-testid="blog-search" />
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-12">
        {loading ? (
          <div className="text-center text-slate-500 py-20">Loading posts…</div>
        ) : posts.length === 0 ? (
          <div className="text-center text-slate-500 py-20" data-testid="blog-empty">
            <BookOpen size={40} className="mx-auto mb-3 text-amber-700/40" />
            <div className="font-display text-2xl text-maroon mb-2">{search ? "No posts match your search" : "No posts yet"}</div>
            <div>{search ? "Try a different keyword." : "Our travel stories will appear here soon."}</div>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="blog-grid">
            {posts.map((post) => (
              <Link key={post.id} to={`/blog/${post.slug || post.id}`} className="group card-warm overflow-hidden flex flex-col" data-testid={`blog-card-${post.id}`}>
                <div className="relative aspect-[16/10] overflow-hidden bg-warm-surface">
                  <img src={fileUrl(post.coverImage) || "/logo.jpg"} alt={post.title}
                    className="w-full h-full object-cover group-hover:scale-[1.05] transition-transform duration-700"
                    onError={(e) => { e.currentTarget.src = "/logo.jpg"; }} />
                </div>
                <div className="p-5 flex-1 flex flex-col">
                  <div className="flex items-center gap-2 text-sm text-slate-500 mb-2">
                    <CalendarDays size={14} /> {formatDate(post.publishedAt)}
                  </div>
                  <h2 className="font-display text-xl font-bold text-maroon leading-snug mb-2 group-hover:text-amber-700 transition">{post.title}</h2>
                  <p className="text-slate-600 text-[15px] leading-relaxed line-clamp-3 flex-1">{post.summary}</p>
                  <div className="mt-4 text-amber-700 text-sm font-bold group-hover:underline">Read article →</div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
